/**
 * WorkshopNGuide – fasilitatorsguide för WorkshopN.
 * Beskriver processen steg för steg, inklusive röstning och rankning.
 */

import React, { useState } from 'react';
import {
  Box, Typography, IconButton, Collapse, Dialog, DialogContent,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CloseIcon from '@mui/icons-material/Close';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import StarIcon from '@mui/icons-material/Star';

const accent = '#E05500';

const PHASES = [
  {
    number: 1,
    title: 'Förberedelse — frågor och grupper',
    duration: '15–30 min (innan)',
    color: '#42A5F5',
    desc: 'En bra workshop börjar med genomtänkta frågor. Undvika ja/nej-frågor — öppna frågor ger mer att arbeta med.',
    tips: [
      'Max 5–7 frågor — för många frågor tröttnar deltagarna',
      'Skapa grupper om ni är fler än 8 — det ger livligare diskussioner',
      'Lägg upp kontextmaterial (bakgrundsdokument, briefinformation) under Inställningar',
      'Testa länken i en webbläsare innan workshopen startar',
    ],
  },
  {
    number: 2,
    title: 'Genomförande — samla in svar',
    duration: '10–45 min',
    color: '#4CAF50',
    desc: 'Deltagarna svarar via sin mobiltelefon eller dator. Svaren visas i realtid på facilitatorns skärm.',
    tips: [
      'Visa QR-koden eller dela länken när alla är på plats',
      'Använd timern per fråga för att hålla tempot — 3–8 min per fråga är lagom',
      'Lås svaren (hänglåset) när du vill stänga insamlingen för en fråga',
      'Hybridläge: skriv ut blad, samla in på papper, skanna sedan med kameran',
      'Kiosk-läge: öppna en separat skärm som visar svar i realtid utan facilitatorkontroller',
    ],
  },
  {
    number: 3,
    title: 'Röstning — prioritera viktigaste svaren',
    duration: '5–10 min',
    color: '#FF9800',
    desc: 'När svaren är insamlade kan ni rösta fram de viktigaste. Varje deltagare och facilitator kan ge upp till 3 stjärnor per fråga.',
    tips: [
      'Öppna Rankning-panelen via trofé-ikonen (★) i verktygsfältet',
      'Facilitatorn röstar med stjärn-ikonen (★) på varje svar — max 3 per fråga',
      'Dela länken med deltagarna och be dem rösta via sin mobil under genomgången',
      'Antal röster visas direkt på svaret i realtid',
      'Tips: gå igenom svaren högt och låt folk rösta medan ni pratar',
    ],
  },
  {
    number: 4,
    title: 'Rankning — sätt guld, silver, brons',
    duration: '2–5 min',
    color: '#FFD700',
    desc: 'Rangordna de tre viktigaste svaren per fråga med medaljer. Detta hjälper AI:n att fokusera analysen på det som gruppen anser vara mest betydelsefullt.',
    tips: [
      'Klicka på trofé-ikonen på ett svar för att sätta guld → silver → brons → ingen (cyclar)',
      'Eller klicka "Beräkna från röster" i Rankning-panelen — automatisk rangordning baserat på stjärnröster',
      'Guld/silver/brons visas tydligt på kortet och skickas med i AI-analysen',
      'Du kan redigera eller ta bort felaktiga svar via penna/papperskorg-ikonerna som visas vid hovring',
      'Rätta felstavningar och slå ihop dubbletter för en renare analys',
    ],
  },
  {
    number: 5,
    title: 'AI-analys — konklusion och åtgärder',
    duration: '5–10 min',
    color: accent,
    desc: 'AI:n analyserar alla svar med hänsyn till röster och rankningar — de viktigaste svaren vägs tyngre. Resultatet inkluderar teman, styrkor, utmaningar och konkreta nästa steg.',
    tips: [
      'Kör analysen efter att röstning och rankning är klara för bäst resultat',
      'Rankade svar (guld/silver/brons) och röstade svar lyfts tyngre i analysen',
      'AI:n tar hänsyn till kontextmaterial och facilitatorbriefs om du lagt in det',
      'Gå vidare till SWOT-analys direkt från workshopen — AI importerar och sorterar svaren automatiskt',
    ],
  },
];

function PhaseCard({ phase }) {
  const [open, setOpen] = useState(false);
  return (
    <Box sx={{ borderRadius: 1.5, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
      <Box
        onClick={() => setOpen(o => !o)}
        sx={{
          display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5,
          cursor: 'pointer', bgcolor: 'action.hover',
          '&:hover': { bgcolor: 'action.selected' },
        }}
      >
        <Box sx={{
          width: 24, height: 24, borderRadius: '50%',
          bgcolor: phase.color + '22', border: `1px solid ${phase.color}44`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <Typography sx={{ fontWeight: 900, fontSize: '0.72rem', color: phase.color }}>{phase.number}</Typography>
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography fontWeight={700} fontSize="0.85rem">{phase.title}</Typography>
          <Typography fontSize="0.68rem" color="text.disabled">{phase.duration}</Typography>
        </Box>
        {open ? <ExpandLessIcon sx={{ fontSize: 16, color: 'text.disabled' }} /> : <ExpandMoreIcon sx={{ fontSize: 16, color: 'text.disabled' }} />}
      </Box>
      <Collapse in={open}>
        <Box sx={{ p: 1.5, pt: 0.5, borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography fontSize="0.82rem" color="text.secondary" lineHeight={1.55} mb={1}>
            {phase.desc}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {phase.tips.map((tip, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
                <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: phase.color, mt: '7px', flexShrink: 0 }} />
                <Typography fontSize="0.78rem" color="text.secondary" lineHeight={1.5}>{tip}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
}

export default function WorkshopNGuide({ onClose }) {
  return (
    <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2.5 }}>

      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
            <HelpOutlineIcon sx={{ color: accent, fontSize: 18 }} />
            <Typography fontWeight={700} fontSize="0.95rem">Guide: Leda en workshop</Typography>
          </Box>
          <Typography fontSize="0.75rem" color="text.secondary">
            Steg-för-steg för dig som faciliterar
          </Typography>
        </Box>
        {onClose && (
          <IconButton size="small" onClick={onClose} sx={{ color: 'text.disabled', mt: -0.5 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      {/* Röstning-highlights */}
      <Box sx={{
        p: 1.75, borderRadius: 1.5,
        bgcolor: '#FFD70010', border: '1px solid #FFD70033',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
          <EmojiEventsIcon sx={{ color: '#FFD700', fontSize: 16 }} />
          <Typography fontSize="0.8rem" fontWeight={700} color="#FFD700">Röstning &amp; rankning — nytt</Typography>
        </Box>
        <Typography fontSize="0.75rem" color="text.secondary" sx={{ lineHeight: 1.55 }}>
          Varje svar kan nu stjärnröstas (max 3 per person per fråga) och rangordnas med guld/silver/brons.
          Rankade och röstade svar vägs tyngre i AI-analysen — det hjälper Claude förstå vad gruppen anser är viktigast.
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5, mt: 1, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <StarIcon sx={{ fontSize: 13, color: '#FFD700' }} />
            <Typography fontSize="0.7rem" color="text.secondary">Hovra på ett svar för att rösta</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <EmojiEventsIcon sx={{ fontSize: 13, color: '#FFD700' }} />
            <Typography fontSize="0.7rem" color="text.secondary">Trofé-ikonen i toolbaren öppnar rankningspanelen</Typography>
          </Box>
        </Box>
      </Box>

      {/* Faser */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {PHASES.map(p => <PhaseCard key={p.number} phase={p} />)}
      </Box>

    </Box>
  );
}

export function WorkshopNHelpButton() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <IconButton size="small" onClick={() => setOpen(true)}
        sx={{ color: 'text.secondary', '&:hover': { color: accent } }}>
        <HelpOutlineIcon fontSize="small" />
      </IconButton>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { maxHeight: '90vh' } }}>
        <DialogContent sx={{ p: 0 }}>
          <WorkshopNGuide onClose={() => setOpen(false)} />
        </DialogContent>
      </Dialog>
    </>
  );
}
